import { useWishlist as useCoreWishlist } from '@shopware/composables'
import { useLocalWishlist, useSyncWishlist } from '#imports'
import type { UseWishlistReturn as BaseUseWishlistReturn } from '@shopware/composables'

export type UseWishlistReturn = Omit<BaseUseWishlistReturn, 'mergeWishlistProducts'> & {
    /** Upstream under-declares this as `void`, even though the implementation is async. */
    mergeWishlistProducts(): Promise<void>
}

/**
 * Override of the upstream `useWishlist`. Its `mergeWishlistProducts` forwards only the
 * local wishlist's ids to the sync merge call, dropping any Excentos configuration a guest
 * stored locally. This wraps it to also collect the stored configs and forward them, so
 * `WishlistSubscriber::onWishlistMerged` can carry them onto the customer's synced wishlist.
 */
export function useWishlist(): UseWishlistReturn {
    const core = useCoreWishlist()
    const { items: itemsLocal, configs: localConfigs } = useLocalWishlist()
    const { mergeWishlistProducts: mergeWishlistProductsSync, getWishlistProducts: getWishlistProductsSync } = useSyncWishlist()

    async function mergeWishlistProducts() {
        if (itemsLocal.value?.length) {
            await mergeWishlistProductsSync(
                itemsLocal.value,
                Object.keys(localConfigs.value).length > 0 ? localConfigs.value : undefined,
            )
            await core.clearWishlist()
        }
        await getWishlistProductsSync()
    }

    return {
        ...core,
        mergeWishlistProducts,
    }
}
